import React, { useState } from 'react';
import { Board } from '../game/Board';
import { ScrollDial } from '../game/ScrollDial';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { ArrowRight, CheckCircle2, RotateCcw } from 'lucide-react';
import { BoardState } from '../../lib/game-logic';
import { cn } from '../../lib/utils';
interface TutorialScreenProps {
  onComplete: () => void;
  onSkip: () => void;
}
export function TutorialScreen({
  onComplete,
  onSkip
}: TutorialScreenProps) {
  const [step, setStep] = useState(0);
  const [board, setBoard] = useState<BoardState>({});
  const [dials, setDials] = useState({
    a: 1,
    b: 1
  });
  const [prevDials, setPrevDials] = useState({
    a: 1,
    b: 1
  });
  const [hasChangedDial, setHasChangedDial] = useState(false);
  const [hasPlacedChip, setHasPlacedChip] = useState(false);
  const [hasChangedSecondDial, setHasChangedSecondDial] = useState(false);
  const [hasReset, setHasReset] = useState(false);
  const steps = [{
    title: 'Welcome to Connectiply!',
    description: "Learn to play in 30 seconds. It's Connect 4 meets multiplication!",
    action: 'Get Started',
    highlight: null
  }, {
    title: 'Step 1: Choose Your Numbers',
    description: 'Tap the dials to pick two numbers (1-9). They multiply to show your target square.',
    action: hasChangedDial ? 'Next Step' : 'Tap a dial to continue',
    highlight: 'dials'
  }, {
    title: 'Step 2: Place Your Chip',
    description: `Your product is ${dials.a * dials.b}. Tap that square on the board to place your chip!`,
    action: hasPlacedChip ? 'Next Step' : 'Place your chip to continue',
    highlight: 'board'
  }, {
    title: 'Step 3: Keep One Dial (Important!)',
    description: 'Now try changing a dial. Notice how the other one locks? You MUST keep one dial the same. But you can reset and pick the other one!',
    action: hasChangedSecondDial && hasReset ? 'Next Step' : 'Change a dial, then reset and change the other',
    highlight: 'dials'
  }, {
    title: 'Step 4: Connect 4 to Win!',
    description: "Get 4 of your chips in a row (horizontal, vertical, or diagonal) to win. That's it!",
    action: 'Start Playing!',
    highlight: null
  }];
  const currentStep = steps[step];
  const product = dials.a * dials.b;
  // Determine locked dials for step 3
  const isALocked = step === 3 && dials.b !== prevDials.b;
  const isBLocked = step === 3 && dials.a !== prevDials.a;
  const handleDialChange = (dial: 'a' | 'b', value: number) => {
    setDials(prev => ({
      ...prev,
      [dial]: value
    }));
    if (step === 1) {
      setHasChangedDial(true);
    }
    if (step === 3) {
      setHasChangedSecondDial(true);
    }
  };
  const handleResetDials = () => {
    setDials(prevDials);
    if (step === 3) {
      setHasReset(true);
    }
  };
  const handlePlaceChip = () => {
    if (step === 2 && !board[product]) {
      setBoard({
        ...board,
        [product]: 1
      });
      setHasPlacedChip(true);
      setPrevDials({
        ...dials
      });
    }
  };
  const handleNext = () => {
    if (step === steps.length - 1) {
      onComplete();
    } else {
      setStep(step + 1);
      if (step === 1) {
        setHasChangedDial(false);
        setPrevDials({
          ...dials
        });
      }
      if (step === 2) {
        setHasPlacedChip(false);
        setHasChangedSecondDial(false);
        setHasReset(false);
      }
    }
  };
  const canProceed = () => {
    if (step === 1) return hasChangedDial;
    if (step === 2) return hasPlacedChip;
    if (step === 3) return hasChangedSecondDial && hasReset;
    return true;
  };
  return <div className="flex flex-col h-full bg-slate-50 p-6" data-id="element-570">
      {/* Skip Button */}
      <div className="flex justify-end mb-4" data-id="element-571">
        <button onClick={onSkip} className="text-sm text-slate-400 hover:text-slate-600 font-semibold" data-id="element-572">
          Skip Tutorial →
        </button>
      </div>

      {/* Progress Dots */}
      <div className="flex justify-center gap-2 mb-6" data-id="element-573">
        {steps.map((_, i) => <div key={i} className={cn('h-2 rounded-full transition-all', i === step ? 'w-8 bg-blue-500' : 'w-2 bg-slate-200', i < step && 'bg-emerald-500')} data-id="element-574" />)}
      </div>

      {/* Instruction Card */}
      <Card className="mb-6 bg-gradient-to-br from-blue-500 to-violet-600 text-white border-none shadow-xl" data-id="element-575">
        <div className="space-y-2" data-id="element-576">
          <div className="flex items-center gap-2" data-id="element-577">
            {step > 0 && step < steps.length - 1 && <span className="text-sm font-bold bg-white/20 px-2 py-1 rounded-full" data-id="element-578">
                Step {step}/{steps.length - 2}
              </span>}
          </div>
          <h2 className="text-2xl font-black" data-id="element-579">{currentStep.title}</h2>
          <p className="text-blue-100 leading-relaxed" data-id="element-580">
            {currentStep.description}
          </p>
        </div>
      </Card>

      {/* Interactive Area */}
      <div className="flex-1 flex flex-col items-center justify-center gap-6 mb-6" data-id="element-581">
        {step >= 1 && <>
            {/* Dials */}
            <div className={cn('flex items-center gap-6 p-4 rounded-2xl transition-all', currentStep.highlight === 'dials' && 'ring-4 ring-blue-300 bg-blue-50')} data-id="element-582">
              <ScrollDial value={dials.a} onChange={v => handleDialChange('a', v)} isLocked={isALocked} label="Dial A" colorClass="bg-violet-100 text-violet-700" data-id="element-583" />
              <span className="text-2xl font-bold text-slate-400" data-id="element-584">×</span>
              <ScrollDial value={dials.b} onChange={v => handleDialChange('b', v)} isLocked={isBLocked} label="Dial B" colorClass="bg-violet-100 text-violet-700" data-id="element-585" />
            </div>

            {/* Reset Button for Step 3 */}
            {step === 3 && (isALocked || isBLocked) && <button onClick={handleResetDials} className="py-2 px-4 text-sm font-semibold text-blue-600 hover:bg-blue-50 rounded-lg transition-colors flex items-center gap-2 ring-2 ring-blue-300 bg-blue-50 animate-pulse" data-id="element-586">
                <RotateCcw size={16} data-id="element-587" />
                Reset Dials (Try This!)
              </button>}

            {step >= 1 && <div className="text-center" data-id="element-588">
                <div className="text-sm font-bold text-slate-500 mb-1" data-id="element-589">
                  Product
                </div>
                <div className="text-5xl font-black text-slate-800" data-id="element-590">
                  {product}
                </div>
              </div>}
          </>}

        {step >= 2 && <div className={cn('transition-all rounded-2xl p-2', currentStep.highlight === 'board' && 'ring-4 ring-blue-300 bg-blue-50')} data-id="element-591">
            <Board boardState={board} highlightProducts={step === 2 ? [product] : []} onSquareClick={handlePlaceChip} disabled={step !== 2} data-id="element-592" />
          </div>}
      </div>

      {/* Action Button */}
      <Button fullWidth size="xl" onClick={handleNext} disabled={!canProceed()} className="gap-2" data-id="element-593">
        {canProceed() && step < steps.length - 1 && <CheckCircle2 size={20} data-id="element-594" />}
        {currentStep.action}
        {step < steps.length - 1 && <ArrowRight size={20} data-id="element-595" />}
      </Button>
    </div>;
}